import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../components/common/PageHeader';
import {
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    Snackbar,
    Chip,
    Divider,
    InputBase,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    FolderOpen as FolderOpenIcon,
    Search as SearchIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { LibraryFolder, LibraryFile } from '../types/library';
import { libraryApi } from '../services/api/libraryApi';

import FolderTreeView from '../components/library/FolderTreeView';
import FileListTable from '../components/library/FileListTable';
import FolderDialog from '../components/library/FolderDialog';
import FileUploadDialog from '../components/library/FileUploadDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';

// Force a real browser download instead of opening the file in a new tab. The
// backend signs the blob URL with Content-Disposition: attachment, so a plain
// top-level navigation downloads the file with no CORS dependency.
const triggerDownload = (url: string, filename: string): void => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
};

// Left padding (theme spacing units) for the tree skeleton rows, faking nesting depth.
const skeletonIndent = (idx: number): number => {
    if (idx % 3 === 1) return 3;
    if (idx % 3 === 2) return 5;
    return 1;
};

// Loading placeholder for the file table; kept out of the page body to limit nesting.
const FileTableSkeleton: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => (
    <TableContainer component={Paper} variant="outlined">
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Document Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {Array.from({ length: 6 }, (_, i) => `file-skeleton-${i}`).map((rowKey) => (
                    <TableRow key={rowKey}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Skeleton variant="circular" width={24} height={24} />
                                <Box>
                                    <Skeleton variant="text" width={160} sx={{ fontSize: '0.875rem' }} />
                                    <Skeleton variant="text" width={120} sx={{ fontSize: '0.75rem' }} />
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell><Skeleton variant="text" width={56} /></TableCell>
                        <TableCell><Skeleton variant="text" width={84} /></TableCell>
                        <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Skeleton variant="circular" width={28} height={28} />
                                {isAdmin && <Skeleton variant="circular" width={28} height={28} />}
                            </Box>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

// Footer summary line for the file table (total count + optional search-match count).
const FileFooterStats: React.FC<{ totalCount: number; filteredCount: number; searchQuery: string }> = ({
    totalCount,
    filteredCount,
    searchQuery,
}) => (
    <Box sx={{ px: 3, py: 1.5, background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">
            {totalCount} file{totalCount === 1 ? '' : 's'} in this folder
        </Typography>
        {searchQuery && (
            <Typography variant="caption" color="text.secondary">
                · {filteredCount} matching search
            </Typography>
        )}
    </Box>
);

const LibraryFilesPage: React.FC = () => {
    const { user } = useAuthStore();
    const isAdmin = (user?.accountType ?? 0) >= AccountType.CarbonCareAdmin;

    const [loading, setLoading] = useState(true);
    const [folders, setFolders] = useState<LibraryFolder[]>([]);
    const [files, setFiles] = useState<LibraryFile[]>([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<LibraryFolder | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [folderDialogOpen, setFolderDialogOpen] = useState(false);
    const [editingFolder, setEditingFolder] = useState<LibraryFolder | null>(null);
    const [parentIdForNewFolder, setParentIdForNewFolder] = useState<number | undefined>();
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({ open: false, title: '', message: '', onConfirm: () => {} });

    const openConfirm = (title: string, message: string, onConfirm: () => void) => {
        setConfirmDialog({ open: true, title, message, onConfirm });
    };
    const closeConfirm = () => setConfirmDialog((prev) => ({ ...prev, open: false }));
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadFolders = useCallback(async () => {
        try {
            const tree = await libraryApi.getFolderTree();
            setFolders(tree);
        } catch (err) {
            console.error('Failed to load folders:', err);
        }
    }, []);

    const loadFiles = useCallback(async (folderId: number) => {
        setFilesLoading(true);
        try {
            const data = await libraryApi.getFilesByFolder(folderId);
            setFiles(data);
        } catch (err) {
            console.error('Failed to load files:', err);
        } finally {
            setFilesLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFolders().finally(() => setLoading(false));
    }, [loadFolders]);

    // Auto-select the first folder once, on initial load.
    const didInitialSelectRef = useRef(false);
    useEffect(() => {
        if (!didInitialSelectRef.current && folders.length > 0) {
            didInitialSelectRef.current = true;
            setSelectedFolder(folders[0]);
        }
    }, [folders]);

    useEffect(() => {
        setSearchQuery('');
        if (selectedFolder) {
            loadFiles(selectedFolder.id);
        } else {
            setFiles([]);
        }
    }, [selectedFolder, loadFiles]);

    const showSnackbar = (message: string, severity?:string) => {
        if(severity){
            setError(message)
        }else{
            setSuccess(message)
        }
    };

    const handleCreateFolder = (parentId?: number) => {
        setEditingFolder(null);
        setParentIdForNewFolder(parentId);
        setFolderDialogOpen(true);
    };

    const handleEditFolder = (folder: LibraryFolder) => {
        setEditingFolder(folder);
        setParentIdForNewFolder(undefined);
        setFolderDialogOpen(true);
    };

    const collectDescendants = (folder: LibraryFolder): LibraryFolder[] => {
        const result: LibraryFolder[] = [];
        if (folder.children) {
            for (const child of folder.children) {
                result.push(...collectDescendants(child), child);
            }
        }
        return result;
    };

    const handleDeleteFolder = (folder: LibraryFolder) => {
        const descendants = collectDescendants(folder);
        openConfirm(
            'Delete Folder',
            `Delete folder "${folder.name}"? All files and folders inside will be permanently deleted.`,
            async () => {
                try {
                    for (const descendant of descendants) {
                        await libraryApi.deleteFolder(descendant.id);
                        if (selectedFolder?.id === descendant.id) setSelectedFolder(null);
                    }
                    await libraryApi.deleteFolder(folder.id);
                    showSnackbar('All data inside the folder is deleted');
                    if (selectedFolder?.id === folder.id) setSelectedFolder(null);
                    await loadFolders();
                } catch {
                    showSnackbar('Failed to delete folder', 'error');
                }
            },
        );
    };

    const handleFolderSubmit = async (data: any) => {
        try {
            if (editingFolder) {
                const result = await libraryApi.updateFolder(editingFolder.id, data);
                showSnackbar('Folder updated');
                if(selectedFolder){
                    setSelectedFolder(result)
                }
            } else {
                await libraryApi.createFolder(data);
                showSnackbar('Folder created');
            }
            await loadFolders();
        } catch(error:any) {
            if( error.response?.data?.error ){
                showSnackbar(error.response?.data?.error, 'error')
            }else{
                showSnackbar('Failed to save folder', 'error');
            }
        }
    };

    const handleDownloadFile = async (file: LibraryFile) => {
        try {
            const result = await libraryApi.downloadFile(file.id);
            if (result.url) {
                // Reflect the server-side download increment without a reload.
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === file.id ? { ...f, download_count: (f.download_count ?? 0) + 1 } : f,
                    ),
                );
                triggerDownload(result.url, result.original_name || file.original_name || file.title);
            }
        } catch {
            showSnackbar('Failed to download file', 'error');
        }
    };

    const handleUploadFile = async (formData: FormData) => {
        await libraryApi.uploadFile(formData);
        showSnackbar('File uploaded successfully');
        if (selectedFolder) await loadFiles(selectedFolder.id);
    };

    const handleDeleteFile = (file: LibraryFile) => {
        openConfirm(
            'Delete File',
            `Delete file "${file.title}"? This action cannot be undone.`,
            async () => {
                try {
                    await libraryApi.deleteFile(file.id);
                    showSnackbar('File deleted');
                    if (selectedFolder) await loadFiles(selectedFolder.id);
                } catch {
                    showSnackbar('Failed to delete file', 'error');
                }
            },
        );
    };

    const filteredFiles = files.filter(
        (f) =>
            !searchQuery ||
            f.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (f.original_name || '').toLowerCase().includes(searchQuery.toLowerCase()),
    );

    return (
        <Box>
            <PageHeader
                description="Files available to you in the document library."
                actions={
                    <>
                        {isAdmin && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleCreateFolder()}
                                sx={{ borderColor: '#90bf45', color: '#90bf45', '&:hover': { borderColor: '#557010', bgcolor: 'rgba(144,191,69,0.06)' } }}
                            >
                                New Folder
                            </Button>
                        )}
                        {isAdmin && selectedFolder && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadDialogOpen(true)}
                            >
                                Upload File
                            </Button>
                        )}
                    </>
                }
            />

            {/* Main content area */}
            <Box sx={{ display: 'flex', gap: 2.5, minHeight: '78vh' }}>
                {/* Sidebar – Folder Tree */}
                <Paper
                    elevation={0}
                    sx={{
                        width: 320,
                        flexShrink: 0,
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Sidebar header */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            background: 'rgba(144,191,69,0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            Folders
                        </Typography>
                        {loading ? (
                            <Skeleton variant="rounded" width={28} height={20} sx={{ borderRadius: '12px' }} />
                        ) : (
                            <Chip label={`${folders.length}`} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(144,191,69,0.12)', color: '#557010', fontWeight: 700 }} />
                        )}
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: 4 } }}>
                        {loading ? (
                            <Box sx={{ p: 1.5 }}>
                                {Array.from({ length: 7 }, (_, i) => `folder-tree-skeleton-${i}`).map((rowKey, idx) => (
                                    <Box
                                        key={rowKey}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 0.75,
                                            pl: skeletonIndent(idx),
                                        }}
                                    >
                                        <Skeleton variant="circular" width={16} height={16} />
                                        <Skeleton variant="text" width={`${50 + ((idx * 13) % 35)}%`} sx={{ fontSize: '0.875rem' }} />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <FolderTreeView
                                folders={folders}
                                selectedFolderId={selectedFolder?.id ?? null}
                                onSelectFolder={setSelectedFolder}
                                onCreateFolder={isAdmin ? handleCreateFolder : undefined}
                                onEditFolder={isAdmin ? handleEditFolder : undefined}
                                onDeleteFolder={isAdmin ? handleDeleteFolder : undefined}
                                isAdmin={isAdmin}
                            />
                        )}
                    </Box>
                </Paper>

                {/* Right – File Content Area */}
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {selectedFolder ? (
                        <>
                            {/* File panel header */}
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2,
                                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                                    background: '#fafbfc',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1f2937' }}>
                                        {selectedFolder.name}
                                    </Typography>
                                    {selectedFolder.description && (
                                        <Typography variant="caption" color="text.secondary">
                                            {selectedFolder.description}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Search bar */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: '#fff',
                                        border: '1.5px solid rgba(0,0,0,0.10)',
                                        borderRadius: 2,
                                        px: 1.5,
                                        height: 36,
                                        width: 240,
                                        flexShrink: 0,
                                        '&:focus-within': { borderColor: '#90bf45', boxShadow: '0 0 0 2px rgba(144,191,69,0.15)' },
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: 17, color: 'text.disabled', mr: 0.75 }} />
                                    <InputBase
                                        placeholder="Search files…"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{ fontSize: '0.85rem', flex: 1 }}
                                    />
                                </Box>
                            </Box>

                            {/* File table */}
                            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                                {filesLoading ? (
                                    <FileTableSkeleton isAdmin={isAdmin} />
                                ) : (
                                    <FileListTable
                                        files={filteredFiles}
                                        onDownload={handleDownloadFile}
                                        onDelete={isAdmin ? handleDeleteFile : undefined}
                                        isAdmin={isAdmin}
                                    />
                                )}
                            </Box>

                            {/* Footer stats */}
                            <Divider />
                            <FileFooterStats
                                totalCount={files.length}
                                filteredCount={filteredFiles.length}
                                searchQuery={searchQuery}
                            />
                        </>
                    ) : (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 4,
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, rgba(144,191,69,0.12) 0%, rgba(86,104,108,0.08) 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <FolderOpenIcon sx={{ fontSize: 40, color: '#90bf45' }} />
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle1" fontWeight={600} color="#4a5660">
                                    Select a folder to view files
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Choose a folder from the sidebar to browse and manage its documents
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Dialogs */}
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={() => { closeConfirm(); confirmDialog.onConfirm(); }}
                onClose={closeConfirm}
            />

            <FolderDialog
                open={folderDialogOpen}
                onClose={() => setFolderDialogOpen(false)}
                onSubmit={handleFolderSubmit}
                folder={editingFolder}
                parentId={parentIdForNewFolder}
            />

            {selectedFolder && (
                <FileUploadDialog
                    open={uploadDialogOpen}
                    onClose={() => setUploadDialogOpen(false)}
                    onSubmit={handleUploadFile}
                    folderId={selectedFolder.id}
                />
            )}

            {/* Success/Error Snackbars */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                {success}
                </Alert>
            </Snackbar>
            
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                {error}
                </Alert>
            </Snackbar>
                </Box>
    );
};

export default LibraryFilesPage;
